import React, { useState } from 'react';
import {
  Grid,
  Button,
  InputLabel,
  FormControl,
  MenuItem,
  OutlinedInput,
  CircularProgress,
  TextareaAutosize,
  Box,
  FormHelperText,
  Checkbox,
  Select,
  Typography,
} from '@mui/material';

import { statuses } from './CreateNewJob';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useMutation } from '@tanstack/react-query';
import { useGetLists, useGetMailwizzLists } from 'services/lists';
import { Job, editJob } from 'services/jobs';
import { daysOfWeek, ispList } from '../../constants';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import momentTZ from 'moment-timezone';
import { DateRange } from '@mui/icons-material';

interface Props {
  onSuccess: () => void;
  job: Job;
  isEditing: boolean;
  setIsEditing: React.Dispatch<React.SetStateAction<boolean>>;
}

const getMinLaunchDateEST = () => momentTZ.tz('America/New_York').subtract(1, 'day').startOf('day').toDate();

const EditJob = ({ onSuccess, job, isEditing, setIsEditing }: Props) => {
  const { data: lists } = useGetLists();
  const { data: mailwizzLists, isLoading: isLoadingListMailwizz } = useGetMailwizzLists();

  const { isError, isLoading, mutate } = useMutation((jobParam: Partial<Job>) => editJob({ ...jobParam, id: job.id }), {
    onError: (error) => console.log(error),
    onSuccess: onSuccess,
  });

  const handleCancel = (setValues: any, initialValues: any) => {
    setIsEditing(false);
    setValues(initialValues);
  };

  // `include_ISP` is stored as a comma-separated string in the DB
  const include_isp: string[] = job?.include_ISP ? job.include_ISP.split(',') : [];
  const [isSubmitting, setIsSubmitting] = useState(false);

  // MailWizz-only validation
  const validationSchema = Yup.object().shape({
    list_ids: Yup.array().required('List is required').min(1),
    name: Yup.string().required('Name is required'),
    scrubbedProfileForEmail: Yup.number().required('Required').lessThan(10000000),
    notes: Yup.string(),
    recurring: Yup.boolean(),
    monday: Yup.boolean(),
    tuesday: Yup.boolean(),
    wednesday: Yup.boolean(),
    thursday: Yup.boolean(),
    friday: Yup.boolean(),
    saturday: Yup.boolean(),
    sunday: Yup.boolean(),
    status: Yup.string(),
    includeISP: Yup.array().required('Include ISP required').min(1, 'At least one ISP required'),
    ispDetails: Yup.object().shape({}),
    mailwizz_list: Yup.string().required('MailWizz List is Required'),
    launchDate: Yup.string().required('Launch Date required'),
  });

  const initialValues: Job = {
    list_ids: job.list_ids || [],
    scrubbedProfileForEmail: job.scrubbedProfileForEmail || 0,
    notes: job.notes || '',
    recurring: job.recurring,
    monday: job.monday || false,
    tuesday: job.tuesday || false,
    wednesday: job.wednesday || false,
    thursday: job.thursday || false,
    friday: job.friday || false,
    saturday: job.saturday || false,
    sunday: job.sunday || false,
    status: job.status,
    name: job.name || '',

    // expose as `launchDate` in the form; backend expects `launch_date`
    launchDate: job.launch_date || '',

    includeISP: include_isp || [],
    ispDetails: job.ispDetails,

    // MailWizz-only fields
    mailwizz_list: job.mailwizz_list || '',
    selected_list: 'mailwizz',

    // legacy/unused fields kept for Job type compatibility
    mailSystemList: '',
    submit: '',
    id: 0,
    day_last_mailed: '',
    include_ISP: '',
    exclude_ISP: '',
    mailwizz_campaign: '',
    launch_date: '',
  };

  const { errors, values, touched, handleBlur, handleChange, handleSubmit, setFieldValue, setStatus, setErrors, setValues } =
    useFormik({
      initialValues,
      validationSchema,
      enableReinitialize: true,
      onSubmit: async (vals) => {
        try {
          setIsSubmitting(true);

          if (vals.list_ids.length > 0 && vals.includeISP.length > 0) {
            const payload: Partial<Job> = {
              list_ids: vals.list_ids.map((x) => Number(x)),
              name: vals.name,
              notes: vals.notes,
              scrubbedProfileForEmail: Number(vals.scrubbedProfileForEmail),

              recurring: vals.recurring,
              monday: vals.monday,
              tuesday: vals.tuesday,
              wednesday: vals.wednesday,
              thursday: vals.thursday,
              friday: vals.friday,
              saturday: vals.saturday,
              sunday: vals.sunday,

              status: vals.status as Job['status'],

              includeISP: vals.includeISP,
              ispDetails: vals.ispDetails as any,

              // MailWizz-only
              selected_list: 'mailwizz',
              mailwizz_list: vals.mailwizz_list,

              // send what the API expects
              launch_date: vals.launchDate,
            };

            mutate(payload);
          }
        } catch (e) {
          if (e instanceof Error) {
            setStatus({ success: false });
            setErrors({ submit: e.message });
          }
        } finally {
          setIsSubmitting(false);
        }
      },
    });

  const getSum = () => {
    let sum = 0;
    values.includeISP.forEach((item: any) => {
      sum += values?.ispDetails[item]?.value ? (values.ispDetails[item]?.value * values.scrubbedProfileForEmail) / 100 : 0;
    });
    return sum;
  };

  return (
    <form noValidate onSubmit={handleSubmit}>
      {lists && mailwizzLists ? (
        <Grid container direction="column" spacing={2} p={2} pb={0} pt={1}>
          {/* Name */}
          <Grid item xs={12} md={12}>
            <InputLabel htmlFor="outlined-adornment-email-login">Name</InputLabel>
            <FormControl fullWidth error={Boolean(touched.name && errors.name)}>
              <OutlinedInput
                id="name"
                type="text"
                disabled={!isEditing}
                name="name"
                value={values.name}
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </FormControl>
          </Grid>

          {/* Lists + Count */}
          <Grid item container spacing={2}>
            <Grid item xs={12} md={6}>
              <InputLabel htmlFor="outlined-adornment-email-login">Select List</InputLabel>
              <FormControl fullWidth>
                <Select
                  multiple
                  id="list_ids"
                  name="list_ids"
                  value={values.list_ids}
                  onBlur={handleBlur}
                  disabled={!isEditing}
                  onChange={(e) => {
                    const { value } = e.target as { value: string[] | string };
                    setFieldValue('list_ids', typeof value === 'string' ? value.split(',') : value);
                  }}
                >
                  {lists.length > 0
                    ? lists.map((list) => (
                        <MenuItem key={list.id} value={list.id}>
                          {list.list_name}
                        </MenuItem>
                      ))
                    : 'No Lists'}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <InputLabel htmlFor="outlined-adornment-email-login">Scrubbed Profiles to be Emailed</InputLabel>
              <FormControl fullWidth error={Boolean(touched.scrubbedProfileForEmail && errors.scrubbedProfileForEmail)}>
                <OutlinedInput
                  id="scrubbedProfileForEmail"
                  disabled={!isEditing}
                  type="number"
                  value={values.scrubbedProfileForEmail}
                  name="scrubbedProfileForEmail"
                  onBlur={handleBlur}
                  onChange={handleChange}
                />
              </FormControl>
            </Grid>
          </Grid>

          {/* Recurring + Days */}
          <Grid item container spacing={2}>
            <Grid item xs={12} md={6}>
              <InputLabel htmlFor="outlined-adornment-email-login">Recurring?</InputLabel>
              <FormControl error={Boolean(touched.recurring && errors.recurring)}>
                <Checkbox
                  checked={values.recurring}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={!isEditing}
                  name="recurring"
                  inputProps={{ 'aria-label': 'controlled' }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6} container spacing={2}>
              {daysOfWeek.map((day) => (
                <Grid item key={day}>
                  <InputLabel htmlFor="outlined-adornment-email-login">{capitalizeFirstLetter(day)}</InputLabel>
                  <FormControl fullWidth error={Boolean((touched as any)[day] && (errors as any)[day])}>
                    <Checkbox
                      disabled={!isEditing}
                      checked={(values as any)[day]}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      name={day}
                      inputProps={{ 'aria-label': 'controlled' }}
                    />
                  </FormControl>
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Status */}
          <Grid item container spacing={2}>
            <Grid item xs={12} md={6}>
              <InputLabel htmlFor="outlined-adornment-email-login">Status</InputLabel>
              <FormControl fullWidth>
                <Select
                  id="status"
                  name="status"
                  value={values.status}
                  onBlur={handleBlur}
                  disabled={!isEditing}
                  onChange={handleChange}
                >
                  {statuses.map((status) => (
                    <MenuItem key={status} value={status.toUpperCase()}>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* MailWizz List + Launch Date */}
          <Grid item container spacing={2}>
            <Grid item xs={12} md={6}>
              <InputLabel htmlFor="outlined-adornment-email-login">MailWizz List</InputLabel>
              <FormControl fullWidth error={Boolean(touched.mailwizz_list && errors.mailwizz_list)}>
                <Select
                  id="mailwizz_list"
                  name="mailwizz_list"
                  value={values.mailwizz_list}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  disabled={!isEditing || isLoadingListMailwizz}
                >
                  {mailwizzLists && mailwizzLists?.count && parseInt(mailwizzLists.count.toString()) > 0 ? (
                    mailwizzLists.records.map((list: any) => (
                      <MenuItem key={list.general.list_uid} value={list.general.list_uid}>
                        {list.general.name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled key={'nodata'} value={''}>
                      {'No Mailwizz List Found'}
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
              {touched.mailwizz_list && errors.mailwizz_list && (
                <FormHelperText error>
                  {typeof errors.mailwizz_list === 'string' ? errors.mailwizz_list : 'An error occurred'}
                </FormHelperText>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <InputLabel htmlFor="outlined-adornment-email-login">Launch Date</InputLabel>
              <FormControl fullWidth error={Boolean(touched.launchDate && errors.launchDate)}>
                <DatePicker
                  className={`date-picker ${touched.launchDate && errors.launchDate ? 'date-border' : ''} date-outline`}
                  minDate={getMinLaunchDateEST()}
                  onChange={(e: any) => {
                    const value = moment(e).format('YYYY-MM-DD');
                    setFieldValue('launchDate', value);
                  }}
                  value={values.launchDate}
                  selected={values?.launchDate ? new Date(moment(values.launchDate).format('YYYY-MM-DD')) : new Date()}
                  showIcon
                  disabled={!isEditing}
                  icon={<DateRange />}
                />
                {touched.launchDate && errors.launchDate && (
                  <FormHelperText sx={{ marginLeft: 0 }} error>
                    {errors.launchDate as string}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>
          </Grid>

          {/* ISP Allocations */}
          <Grid item container spacing={2}>
            <Grid item xs={12} md={6}>
              <InputLabel htmlFor="outlined-adornment-email-login">Include ISP</InputLabel>
              {values.includeISP && values.includeISP.length > 0 ? (
                <FormControl fullWidth>
                  <Select
                    multiple
                    id="includeISP"
                    name="includeISP"
                    value={values.includeISP || []}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    disabled={!isEditing}
                  >
                    {ispList.map((list) => (
                      <MenuItem key={list} value={list} selected={values.includeISP.includes(list)}>
                        {list === 'None' ? 'All Not G&M' : list}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                ''
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <InputLabel htmlFor="outlined-adornment-email-login">Number Of ISP</InputLabel>
              {values.includeISP && values.includeISP.length > 0 ? (
                <Box>
                  {values.includeISP.map((name: any, index: number) => (
                    <Box key={index} sx={{ display: 'flex', gap: 2, mb: '10px', justifyContent: 'left', alignItems: 'center' }}>
                      <FormControl fullWidth error={Boolean(touched.ispDetails && errors.ispDetails)}>
                        <OutlinedInput
                          id="outlined-adornment-email-login"
                          type="text"
                          name={`ispDetails.${name}.value`}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.ispDetails[name]?.value}
                          disabled={!isEditing}
                        />
                      </FormControl>
                      <Typography>%</Typography>
                      <Typography>{name}</Typography>
                      <Box>
                        {values.ispDetails[name]?.value
                          ? (values.ispDetails[name]?.value * values.scrubbedProfileForEmail) / 100
                          : 0}
                      </Box>
                    </Box>
                  ))}
                  <Box sx={{ display: 'flex', gap: 2, mb: '10px', justifyContent: 'right', alignItems: 'center' }}>
                    <Box>{getSum()}</Box>
                  </Box>
                </Box>
              ) : (
                ''
              )}
            </Grid>
          </Grid>

          {/* Notes */}
          <Grid item>
            <InputLabel htmlFor="outlined-adornment-email-login">Notes</InputLabel>
            <FormControl fullWidth error={Boolean(touched.notes && errors.notes)}>
              <TextareaAutosize name="notes" minRows={3} onChange={handleChange} onBlur={handleBlur} disabled={!isEditing} />
            </FormControl>
          </Grid>
        </Grid>
      ) : (
        <Box>
          <p></p>
        </Box>
      )}

      {isError && (
        <Box sx={{ mt: 3 }}>
          <FormHelperText error>{'Failed to Edit Job'}</FormHelperText>
        </Box>
      )}

      <Grid item container spacing={2} pt={2}>
        {isEditing ? (
          <>
            <Grid item xs={12} md={6}>
              <Button
                sx={{ mt: 2 }}
                disableElevation
                fullWidth
                size="large"
                onClick={() => handleCancel(setValues, initialValues)}
                variant="contained"
                color="warning"
                disabled={isLoading}
              >
                {'Cancel'}
              </Button>
            </Grid>
            <Grid item xs={12} md={6}>
              <Button
                sx={{ mt: 2 }}
                disableElevation
                fullWidth
                size="large"
                type="submit"
                color="info"
                variant="contained"
                disabled={isLoading || isSubmitting}
              >
                {isLoading ? <CircularProgress /> : 'Update'}
              </Button>
            </Grid>
          </>
        ) : (
          <Button
            sx={{ mt: 3 }}
            disableElevation
            fullWidth
            size="large"
            variant="contained"
            onClick={() => setIsEditing(true)}
            color="primary"
          >
            {'Edit Job'}
          </Button>
        )}
      </Grid>
    </form>
  );
};

function capitalizeFirstLetter(string: string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export default EditJob;
